<template>
    <ModalRouteChild :parent="parent" resume>
        <template v-if="showErrorStep">
            <SectionTitle
                :title="config.ERROR_TITLE"
                :description="config.ERROR_DESCRIPTION"
            />
            <SectionPicture>
                <ArtError :color="config.COLOR_PRIMARY"/>
            </SectionPicture>
            <SectionText :text="config.UPLOAD_ERROR"/>
            <SectionActions>
                <Button :color="config.COLOR_PRIMARY" @click="onRetry">
                    {{ config.ERROR_RETRY }}
                </Button>
            </SectionActions>
        </template>
        <template v-else>
            <SectionTitle
                :title="config.UPLOAD_TITLE"
                :description="config.UPLOAD_DESCRIPTION"
            />
            <section class="upload">
                <div id="upload-widget"/>
                <div v-if="errorMessage" class="error-message">
                    {{ errorMessage }}
                </div>
                <Button :color="config.COLOR_PRIMARY" :disabled="!uploaded" @click="onUploadDone">
                    {{ config.UPLOADER_VALIDATE }}
                </Button>
            </section>
        </template>
    </ModalRouteChild>
</template>
<script>
import { captureException } from '@sentry/browser';

import {
    SolutionConsumer, ProjectConsumer, UserConsumer, CoreConsumer,
} from '../../mixins';
import { ModalRouteChild } from '../../components/desktop';
import {
    SectionPicture, SectionTitle, SectionText, SectionActions,
} from '../../components/shared';
import '@skeepers/upload-widget';
import { parseGraphqlError } from '../../utils';

export default {
    name: 'UploadModal',
    components: {
        ModalRouteChild, SectionPicture, SectionTitle, SectionText, SectionActions,
    },
    mixins: [SolutionConsumer, ProjectConsumer, UserConsumer, CoreConsumer],
    props: {
        parent: {
            type: String,
            default: 'deposit.brief',
        },
        next: {
            type: String,
            default: 'deposit.thanks',
        },
        isBookPlatform: {
            type: Boolean,
            default: false,
        },
        isProductPlatform: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            uploaded: false,
            showErrorStep: null,
            errorMessage: null,
        };
    },
    mounted () {
        window.skeepers.uploader(document.getElementById('upload-widget'), {
            text: {
                download: this.config.UPLOADER_DOWNLOAD,
                add: this.config.UPLOADER_ADD,
                edit: this.config.UPLOADER_EDIT,
                delete: this.config.UPLOADER_DELETE,
                dragAndDrop: this.config.UPLOADER_DRAG_AND_DROP,
                or: this.config.UPLOADER_OR,
                upload: this.config.UPLOADER_UPLOAD,
                invalidTypes: this.config.UPLOADER_INVALID_TYPES,
                hoursLeft: this.config.UPLOADER_HOURS_LEFT,
                minutesLeft: this.config.UPLOADER_MINUTES_LEFT,
                secondsLeft: this.config.UPLOADER_SECONDS_LEFT,
                longRemaningTime: this.config.UPLOADER_LONG_REMAINING_TIME,
                shortRemaningTime: this.config.UPLOADER_SHORT_REMAINING_TIME,
            },
            config: {
                API_URL: this.apiUrl,
                MEDIAINFO_URL: this.mediainfoUrl,
            },
            onChange: (value) => {
                this.errorMessage = null;
                this.files = this.uploadOptions.multiple ? value : [value];
            },
            multiple: this.uploadOptions.multiple,
            filenamesMode: 'READONLY',
            type: 'FILE',
            primaryColor: this.config.COLOR_PRIMARY,
            validTypes: this.uploadOptions.validTypes,
            lock: () => {
                this.uploaded = false;
            },
            unlock: () => {
                if (this.files && this.files.length > 0) {
                    this.uploaded = true;
                }
            },
        });
    },
    methods: {
        async onUploadDone () {
            this.uploaded = false;
            try {
                await this.uploadFiles({
                    files: this.files,
                    ...(this.isBookPlatform || this.isProductPlatform ? { productId: this.$route.params.id } : {}),
                    isProductPlatform: this.isProductPlatform,
                });
                this.$router.push({ name: this.next });
            } catch (err) {
                console.log(err.stack);
                const errorMessage = parseGraphqlError(err, this.config);
                if (errorMessage) {
                    this.errorMessage = errorMessage;
                } else {
                    this.showErrorStep = err;
                }
                captureException(err);
            }
        },
        onRetry () {
            this.showErrorStep = null;
        },
    },
};
</script>
<style lang="scss" scoped>
@import '../../assets/scss/mixins';
@import '../../assets/scss/colors';

section {
    text-align: center;

    button {
        margin: 20px auto 0;
    }

    &.upload {
      > .error-message {
        color: $red
      }
    }
}
</style>
